import { ApiError } from '@/app/lib/apiError'
import { useMemo, useState } from 'react'
import { Link, Navigate, useNavigate, useOutletContext } from 'react-router'

import { QuantLogPanel } from '@/app/components/QuantLogPanel'
import { QuantRunParams } from '@/app/components/QuantRunParams'
import { useNowTick } from '@/app/hooks/useNowTick'
import { removeLiveSession } from '@/app/hooks/useQuantLiveSessions'
import { useQuantLiveEquity } from '@/app/hooks/useQuantLiveEquity'
import { useQuantLiveEvents } from '@/app/hooks/useQuantLiveEvents'
import { useQuantLiveLog } from '@/app/hooks/useQuantLiveLog'
import { useQuantLiveSession } from '@/app/hooks/useQuantLiveSession'
import {
  EVENTS_DOM_LIMIT,
  EVENTS_MEMORY_LIMIT,
  eventSummary,
  eventTime,
  newestFirst,
} from '@/app/lib/quant/liveEvents'
import { flagsFor, saveConfigForm, setLoadedNote, saveAccountForm } from '@/app/lib/quant/configFormState'
import { accountFormFrom } from '@/app/lib/quant/liveAccount'
import { paramsForConfig } from '@/app/lib/quant/paramsForConfig'
import { FIGURES_CAPTION, NOT_FINAL_CAPTION, STOP_EXPLANATION } from '@/app/lib/quant/liveCopy'
import {
  activityLine,
  elapsedText,
  endedCopy,
  figureCells,
  finalRows,
  formatMoney,
  isEnded,
  localClock,
  stateAge,
  stateProblemText,
  stopControl,
} from '@/app/lib/quant/liveView'
import { quantApi } from '@/app/lib/quant/quantApi'
import { isLiveGone, isLiveStarting, quantErrorMessage } from '@/app/lib/quant/quantErrors'
import { schemaToParams } from '@/app/lib/quant/schemaToParams'
import { type FeedState, type FeedSymbol, type LiveSession, type ParamDef, type ParamValue, type Strategy } from '@/app/lib/quant/types'
import type { QuantShellContext } from '@/app/pages/QuantJupyter'
import { useSignalLog } from '@/app/lib/signalLog'
import { Button } from '@/ui/Button'
import { ConfirmButton } from '@/ui/ConfirmButton'
import { cn } from '@/ui/lib/utils'
import { QuantEquityChart } from '@/ui/QuantEquityChart'
import { QuantLiveBadge } from '@/ui/QuantLiveBadge'
import { QuantLiveEventRow } from '@/ui/QuantLiveEventRow'
import { QuantLiveFigures } from '@/ui/QuantLiveFigures'
import { QuantUnavailable } from '@/ui/QuantUnavailable'

function BackLink({ slug }: { slug: string }) {
  return (
    <Link
      to={`/strategies/${slug}/live`}
      aria-label="Back to live sessions"
      className="font-mono text-[11px] tracking-[0.1em] text-paper-50 uppercase no-underline hover:text-paper"
    >
      ← Live sessions
    </Link>
  )
}

type SessionTab = 'events' | 'log' | 'params'
const TABS: Array<[SessionTab, string]> = [
  ['events', 'Events'],
  ['log', 'Log'],
  ['params', 'Params'],
]
const EVENTS_MORE = 100
const EQUITY_EMPTY =
  'No equity line yet. The first point is written when the first 1-minute bar closes.'

const plural = (n: number, one: string, many: string): string => (n === 1 ? one : many)

/** "Paper account: balance 100,000.00 · commission 2.50 per contract · max 1 contract". */
function accountCaption(session: LiveSession): string {
  const a = session.account
  const max = a?.max_contracts
  const maxText = typeof max === 'number' ? `${max} ${plural(max, 'contract', 'contracts')}` : '—'
  return `Paper account: balance ${formatMoney(a?.starting_balance)} · commission ${formatMoney(a?.commission_per_contract)} per contract · max ${maxText}`
}

/**
 * The live session detail (spec 041 P4): back link, a fixed 72 px summary strip, the ended
 * block, the figures panel and the Params tab. The status badge shows `status` only and the
 * activity line `state.activity` only; they are never merged. Stop and Delete are two-press
 * confirms; every write merges the server's answer and never guesses a result.
 */
export function QuantLiveDetail({
  strategy,
  sessionId,
}: {
  strategy: Strategy
  sessionId: string
}) {
  const live = useQuantLiveSession(sessionId)
  if (live.status === 'loading') return <div data-pane="live" className="absolute inset-0" />
  if (live.status === 'not_found') {
    return (
      <div
        data-pane="live"
        className="absolute inset-0 flex flex-col gap-3 px-7 pt-6 font-mono text-[12px] text-paper-50"
      >
        <span role="status">This session no longer exists.</span>
        <BackLink slug={strategy.slug} />
      </div>
    )
  }
  if (live.status === 'error') {
    return (
      <div
        data-pane="live"
        className="absolute inset-0 flex flex-col gap-3 px-7 pt-6 font-mono text-[12px]"
      >
        <QuantUnavailable message={live.message} onRetry={live.reload} />
        <BackLink slug={strategy.slug} />
      </div>
    )
  }
  if (live.session.strategy_slug !== strategy.slug) {
    return (
      <Navigate
        replace
        to={`/strategies/${live.session.strategy_slug}/live/${live.session.id}`}
      />
    )
  }
  return (
    <SessionView
      strategy={strategy}
      session={live.session}
      pollError={live.error}
      reload={live.reload}
      merge={live.merge}
      active={live.active}
    />
  )
}

interface SessionViewProps {
  strategy: Strategy
  session: LiveSession
  pollError?: string
  reload: () => void
  merge: (session: LiveSession) => void
  active: boolean
}

function SessionView({ strategy, session, pollError, reload, merge, active }: SessionViewProps) {
  const navigate = useNavigate()
  const { emit } = useSignalLog()
  const shell = useOutletContext<QuantShellContext | undefined>()
  const now = useNowTick(active)
  const [actionError, setActionError] = useState<string | null>(null)
  const [tab, setTab] = useState<SessionTab>('events')
  const events = useQuantLiveEvents(session.id, active)
  const equity = useQuantLiveEquity(session.id, active)

  const feed = shell?.feed
  const shellFeedState: FeedState | null = feed?.status === 'loaded' ? feed.state : null
  const shellSymbols: FeedSymbol[] | null =
    feed?.status === 'loaded' ? (feed.feed.symbols ?? null) : null

  const ended = isEnded(session)
  const activity = activityLine(session, shellFeedState)
  const control = stopControl(session)
  const age = stateAge(session)
  const problem = stateProblemText(session, now)
  const ageLine = problem ?? age.text
  const cells = figureCells(session, { shellFeedState, shellSymbols })
  const ending = ended ? endedCopy(session) : null
  const final = ended ? finalRows(session) : null
  const caption = ended && session.final === null ? NOT_FINAL_CAPTION : FIGURES_CAPTION
  const stopLine = control.reason ?? STOP_EXPLANATION

  const schemaDefs = useMemo(() => schemaToParams(strategy.params_schema), [strategy.params_schema])
  const noSchema = schemaDefs.length === 0 && Object.keys(session.params).length > 0
  const defs = useMemo<ParamDef[]>(
    () =>
      noSchema
        ? Object.entries(session.params).map(([name, value]) => ({
            name,
            kind: typeof value === 'boolean' ? 'bool' : typeof value === 'number' ? 'float' : 'str',
            default: value,
            help: '',
            options: [],
          }))
        : schemaDefs,
    [noSchema, session.params, schemaDefs],
  )
  const ignoredNames = useMemo(() => (session.ignored ?? []).map((i) => i.name), [session.ignored])

  async function stop() {
    setActionError(null)
    try {
      const answer = await quantApi.stopLiveSession(session.id)
      merge(answer)
      emit('save', `Stop requested for live session ${session.id}.`)
    } catch (error) {
      if (isLiveStarting(error) && error instanceof ApiError) {
        setActionError(error.message)
        reload()
      } else if (isLiveGone(error)) {
        reload()
      } else {
        setActionError(quantErrorMessage(error))
      }
    }
  }

  async function remove() {
    setActionError(null)
    try {
      await quantApi.deleteLiveSession(session.id)
    } catch (error) {
      // A 404 means it is already gone, which is what the operator wanted.
      if (!isLiveGone(error)) {
        if (error instanceof ApiError && error.status === 409) {
          setActionError(error.message)
          reload()
        } else {
          setActionError(quantErrorMessage(error))
        }
        return
      }
    }
    removeLiveSession(session.id)
    emit('save', `Deleted live session ${session.id}.`)
    navigate(`/strategies/${strategy.slug}/live`)
  }

  function loadParams() {
    const raw: Record<string, ParamValue> = {}
    const { kept, dropped } = paramsForConfig(session.params, schemaDefs)
    for (const def of defs) {
      const value = def.name in kept ? kept[def.name] : def.default
      raw[def.name] = def.kind === 'bool' ? value === true : value === null ? '' : String(value)
    }
    saveConfigForm(strategy.slug, { raw, lastValidFlags: flagsFor(defs, raw) ?? '' })
    setLoadedNote(strategy.slug, dropped)
    saveAccountForm(strategy.slug, accountFormFrom(session.account))
    emit('save', `Loaded live session ${session.id} params into the ${strategy.slug} config.${dropped.length > 0 ? ` (${dropped.length} dropped)` : ''}`)
    navigate(`/strategies/${strategy.slug}/config`)
  }

  const name = `${session.strategy_slug} · ${session.instrument}`
  return (
    <div
      data-pane="live"
      data-live-detail={session.id}
      className="absolute inset-0 flex min-h-0 flex-col overflow-y-auto"
    >
      <div className="flex shrink-0 items-center gap-3.5 px-7 pt-3.5 pb-2.5">
        <BackLink slug={strategy.slug} />
      </div>
      {pollError !== undefined && (
        <div className="shrink-0 px-7 pb-2.5">
          <QuantUnavailable message={pollError} onRetry={reload} />
        </div>
      )}

      <div
        data-live-summary=""
        className="box-border flex h-[72px] shrink-0 items-center gap-4 border-y border-paper-14 px-7"
      >
        <div className="flex min-w-0 flex-1 flex-col font-mono">
          <div className="flex h-5 min-w-0 items-center gap-3 text-[11px]">
            <QuantLiveBadge status={session.status} />
            <span className="min-w-0 shrink truncate text-paper-90" title={name}>
              {name}
            </span>
            <span className="w-[200px] shrink-0 truncate text-paper-40" title={session.id}>
              {session.id}
            </span>
            <span className="shrink-0 text-paper-50">Started {localClock(session.started_at)}</span>
            <span className="shrink-0 text-paper-50">{elapsedText(session, now)}</span>
          </div>
          <div
            data-activity-line=""
            title={activity.text}
            className={cn(
              'h-5 truncate text-[11px] leading-5',
              activity.tone === 'warn' ? 'text-[#CB8A00]' : 'text-paper-50',
            )}
          >
            {activity.text}
          </div>
          <div className="flex h-5 min-w-0 items-center gap-3 text-[11px]">
            <span
              data-stop-line=""
              title={stopLine}
              className="min-w-0 flex-1 truncate text-paper-50"
            >
              {stopLine}
            </span>
            {actionError !== null && (
              <span
                role="alert"
                title={actionError}
                className="max-w-[40%] shrink-0 truncate text-alert"
              >
                {actionError}
              </span>
            )}
          </div>
        </div>
        <div className="shrink-0">
          {control.kind === 'delete' ? (
            <ConfirmButton
              label="DELETE"
              size="sm"
              ariaLabel="Delete session"
              armedAriaLabel="Confirm delete session"
              className="min-w-[104px]"
              onArm={() =>
                emit('warn', 'Delete armed for this live session - press again to confirm.')
              }
              onConfirm={() => void remove()}
            />
          ) : control.label === 'Stopping…' ? (
            <Button size="sm" disabled className="min-w-[104px]">
              Stopping…
            </Button>
          ) : (
            <span title={control.reason ?? undefined}>
              <ConfirmButton
                label="STOP"
                size="sm"
                ariaLabel="Stop session"
                armedAriaLabel="Confirm stop session"
                disabled={!control.enabled}
                className="min-w-[104px]"
                onArm={() =>
                  emit('warn', 'Stop armed for this live session - press again to confirm.')
                }
                onConfirm={() => void stop()}
              />
            </span>
          )}
        </div>
      </div>

      {ending !== null && (
        <div
          data-live-ended=""
          className="box-border max-h-[176px] shrink-0 overflow-y-auto border-b border-paper-14 px-7 py-3 font-mono"
        >
          <div className="text-[13px] font-bold text-paper">{ending.headline}</div>
          {ending.sentence !== null && (
            <div className="mt-1 text-[12px] text-paper-90">{ending.sentence}</div>
          )}
          {ending.showErrorBlock && session.error !== null && (
            <pre className="mt-2 mb-0 max-h-[72px] overflow-auto bg-ink-14 px-3 py-2 text-[11px] whitespace-pre-wrap text-paper-90 [overflow-wrap:anywhere]">
              {session.error}
            </pre>
          )}
          {ending.reasonCode !== null && (
            <div className="mt-1 text-[10px] text-paper-40">{ending.reasonCode}</div>
          )}
        </div>
      )}
      {final !== null && (
        <div
          data-live-final=""
          className="grid shrink-0 grid-cols-3 border-b border-paper-14 px-7 py-2 font-mono"
        >
          {final.map((row) => (
            <div
              key={row.key}
              data-final-row={row.key}
              className="flex h-7 min-w-0 items-center justify-between gap-3 text-[11px]"
              title={`${row.label} ${row.value}`}
            >
              <span className="truncate text-paper-40">{row.label}</span>
              <span className="truncate text-paper-90">{row.value}</span>
            </div>
          ))}
        </div>
      )}

      <div className="shrink-0 px-7 pt-2">
        <QuantLiveFigures cells={cells} caption={caption} stale={age.stale} ageLine={ageLine} />
      </div>

      <div
        role="group"
        aria-label="Equity line"
        data-live-equity=""
        className="mx-7 mt-2 box-border flex h-[160px] shrink-0 flex-col overflow-hidden border border-paper-14 font-mono"
      >
        <div className="relative min-h-0 flex-1 bg-ink">
          {equity.points.length >= 2 ? (
            <QuantEquityChart points={equity.points} label="EQUITY · PAPER" className="h-full" />
          ) : (
            <div
              data-equity-empty=""
              className="absolute inset-0 flex items-center justify-center px-4 text-center text-[12px] text-paper-50"
            >
              {EQUITY_EMPTY}
            </div>
          )}
        </div>
        <div
          data-equity-caption=""
          className="h-5 shrink-0 truncate px-3 text-[10px] leading-5 text-paper-50"
        >
          {equity.problem !== null
            ? `Equity unavailable: ${equity.problem}.`
            : equity.truncated
              ? `Thinned to ${equity.points.length} of ${equity.total} points.`
              : null}
        </div>
      </div>

      <div
        role="tablist"
        aria-label="Session panels"
        className="mt-2 flex h-10 shrink-0 gap-5 border-b border-paper-14 px-7"
      >
        {TABS.map(([id, label]) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            onClick={() => setTab(id)}
            className={cn(
              'cursor-pointer border-0 border-b-2 bg-transparent py-0 font-mono text-[11px] tracking-[0.14em] uppercase',
              tab === id
                ? 'border-accent-line text-paper'
                : 'border-transparent text-paper-50 hover:text-paper',
            )}
          >
            {label}
          </button>
        ))}
      </div>
      <div
        data-live-panel=""
        className="relative box-border h-[336px] shrink-0 overflow-hidden border-b border-paper-14"
      >
        {tab === 'events' && (
          <EventsPanel events={events} totalEvents={session.state?.counts?.events ?? null} />
        )}
        {tab === 'log' && <LiveLogTab id={session.id} active={active} />}
        {tab === 'params' && (
          <>
            <div
              data-account-caption=""
              className="flex h-7 items-center truncate px-7 font-mono text-[11px] text-paper-50"
              title={accountCaption(session)}
            >
              <span className="truncate">{accountCaption(session)}</span>
            </div>
            <QuantRunParams
              defs={defs}
              params={session.params}
              noSchema={noSchema}
              onLoadParams={loadParams}
              ignoredNames={ignoredNames}
            />
          </>
        )}
      </div>
    </div>
  )
}

function LiveLogTab({ id, active }: { id: string; active: boolean }) {
  // Mounted only while the Log tab is selected, so opening it replays the file from offset 0.
  const log = useQuantLiveLog(id, true, active)
  return <QuantLogPanel log={log} ariaLabel="Session log" />
}

function EventsPanel({
  events,
  totalEvents,
}: {
  events: ReturnType<typeof useQuantLiveEvents>
  totalEvents: number | null
}) {
  const [shown, setShown] = useState(EVENTS_DOM_LIMIT)
  const rows = newestFirst(events.events, shown)
  const older = events.events.length - rows.length
  const memory = EVENTS_MEMORY_LIMIT.toLocaleString('en-US')
  const dropped =
    totalEvents !== null && Number.isFinite(totalEvents)
      ? `Showing the newest ${memory} of ${totalEvents.toLocaleString('en-US')}.`
      : `Showing the newest ${memory}.`
  const notes: string[] = []
  if (events.skipped > 0) notes.push(`${events.skipped} event lines could not be read.`)
  if (events.problem !== null) notes.push(`Events unavailable: ${events.problem}.`)
  if (events.droppedOlder) notes.push(dropped)
  return (
    <div data-events-panel="" className="absolute inset-0 flex flex-col font-mono">
      <div className="min-h-0 flex-1 overflow-y-auto">
        {events.error !== undefined && events.events.length === 0 && (
          <div className="px-7 py-3 text-[11px] text-paper-50">
            Events could not be read: {events.error}
          </div>
        )}
        {events.loaded && events.events.length === 0 && events.error === undefined && (
          <div className="px-7 py-3 text-[11px] text-paper-50">No events yet.</div>
        )}
        <ul aria-label="Session events" className="m-0 list-none p-0">
          {rows.map((event) => {
            const summary = eventSummary(event)
            return (
              <QuantLiveEventRow
                key={event.seq}
                seq={event.seq}
                time={eventTime(event)}
                text={summary.text}
                kind={summary.kind}
                muted={summary.muted}
              />
            )
          })}
        </ul>
        {older > 0 && (
          <div className="px-3 py-2">
            <Button variant="secondary" size="sm" onClick={() => setShown((n) => n + EVENTS_MORE)}>
              Show 100 older
            </Button>
          </div>
        )}
      </div>
      {notes.length > 0 && (
        <div
          data-events-notes=""
          className="shrink-0 border-t border-paper-14 px-3 py-1.5 text-[10px] leading-4 text-paper-50"
        >
          {notes.map((note) => (
            <div key={note} className="truncate" title={note}>
              {note}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
